import { SystemBase4 } from '@inter-ikea-core/core-toolbox';
import { ComponentIds, ModelUtils } from '@inter-ikea-core/core';
import { getIpexViewer } from '@inter-ikea-core/ipex-betrakta-core-adapter';
import { entityIsVisible } from './HideOccludingObjectsSystem';
import { HideOccludingObjectsComponentIds } from './types';
import ToggleDoorsComponent from '../../components/ToggleDoorsComponent';
const TRANSPARENT_MATERIAL_OPACITY = 0.15;
const FADE_OUT_TIME = 400;
const FADE_OUT_UPDATE_TIME = 30;
/**
 * The system handles the objects that need to be faded away by applying a
 * transparent material. It also restores the objects original material
 * when they are no longer hidden.
 */
export class FadeMaterialSystem extends SystemBase4 {
    get viewer() {
        return getIpexViewer(this.coreServices.sceneManager);
    }
    constructor(coreServices) {
        super(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID, HideOccludingObjectsComponentIds.FADE_ANIMATION_COMPONENT_ID, ComponentIds.WORLD_SPACE_DRAG_EVENT_COMPONENT_ID, ComponentIds.UPDATED_FROM_SNAPSHOT_EVENT_COMPONENT_ID);
        this.coreServices = coreServices;
        this.id = 'FadeMaterialSystem';
        this.systemsAfterMe = [];
        this.systemsBeforeMe = [];
        this.doingChangesOnComponent = [];
        this.cameraUpdatedObserver = null;
        this.lastPosition = null;
        this.lastForward = null;
        this.cameraMovementThreshold = 0.001;
        this.transparentMaterialId = -1;
        this.materialFadeInterval = -1;
        this.materialOpacity = TRANSPARENT_MATERIAL_OPACITY;
    }
    enable() {
        if (!this.viewer)
            return;
        super.enable();
        if (this.transparentMaterialId === -1) {
            this.transparentMaterialId = this.viewer.create_material();
            this.viewer.set_base_color(this.transparentMaterialId, { r: 1.0, g: 1.0, b: 1.0 });
            this.viewer.set_opacity(this.transparentMaterialId, this.materialOpacity);
            this.viewer.set_unlit(this.transparentMaterialId, true);
        }
        // Store initial camera position and direction
        const initialState = this.viewer.get_camera_state();
        if (initialState) {
            this.lastPosition = { ...initialState.position };
            this.lastForward = { ...initialState.forward };
        }
        // Only trigger showFadedEntities when the camera has actually moved
        this.cameraUpdatedObserver = this.viewer.get_camera_updated_observable().add(() => {
            const currentState = this.viewer?.get_camera_state();
            if (!currentState)
                return;
            if (this.hasCameraPositionChanged(currentState)) {
                this.showFadedEntities();
                // Update the stored position and direction
                this.lastPosition = { ...currentState.position };
                this.lastForward = { ...currentState.forward };
            }
        });
    }
    disable() {
        this.cameraUpdatedObserver?.dispose();
        this.cameraUpdatedObserver = null;
        super.disable();
    }
    onAddComponent(entity, _newComponent) {
        this.applyTransparentMaterial(entity);
    }
    onRemoveComponent(entity, _oldComponent) {
        this.removeTransparentMaterial(entity);
    }
    // Override methods to track entity selection events
    onAddComponent2(scene, fadeAnimationComponent) {
        if (fadeAnimationComponent.playAnimation) {
            this.startFadeMaterialAnimation();
        }
        else {
            this.stopFadeMaterialAnimation();
        }
        scene.removeComponent(HideOccludingObjectsComponentIds.FADE_ANIMATION_COMPONENT_ID);
    }
    onUpdateComponent3(_entity, _oldComponent, _newComponent) {
        const dragState = this.coreServices.sceneManager.scene.getComponent(ComponentIds.DRAG_STATE_COMPONENT)?.dragState;
        if (!dragState)
            return;
        const { originalDraggedEntity, initialTransform } = dragState;
        if (!entityIsVisible(originalDraggedEntity, this.viewer, initialTransform)) {
            this.showFadedEntities();
        }
    }
    onAddComponent4(entity, newComponent) {
        const before = newComponent.entityStateBeforeLoadingSnapshot?.getComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
        const after = newComponent.entity?.getComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID);
        if (before && !after)
            this.onRemoveComponent(entity, before);
        if (!before && after)
            this.onAddComponent(entity, after);
    }
    /**
     * Determines if the camera has moved by comparing position and direction vectors
     * @param currentState The current camera state
     * @returns true if camera has moved beyond the threshold
     */
    hasCameraPositionChanged(currentState) {
        if (!this.lastPosition || !this.lastForward)
            return false;
        const { position: newPos, forward: newFwd } = currentState;
        // Check for significant changes in position
        const positionChanged = Math.abs(this.lastPosition.x - newPos.x) > this.cameraMovementThreshold ||
            Math.abs(this.lastPosition.y - newPos.y) > this.cameraMovementThreshold ||
            Math.abs(this.lastPosition.z - newPos.z) > this.cameraMovementThreshold;
        // Check for significant changes in direction
        const directionChanged = Math.abs(this.lastForward.x - newFwd.x) > this.cameraMovementThreshold ||
            Math.abs(this.lastForward.y - newFwd.y) > this.cameraMovementThreshold ||
            Math.abs(this.lastForward.z - newFwd.z) > this.cameraMovementThreshold;
        return positionChanged || directionChanged;
    }
    entityHasHoverFocusOrSelected(entity) {
        return entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID);
    }
    showFadedEntities() {
        const entitiesToShow = this.coreServices.entityManager.entities.filter(entity => entity.hasComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID) &&
            !entity.getComponent(ToggleDoorsComponent.id)?.doorsHidden);
        entitiesToShow?.forEach(entity => {
            ModelUtils.show(entity, false);
        });
    }
    hideFadedEntities() {
        this.coreServices.entityManager.entities
            .filter(entity => entity.hasComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID))
            .forEach(entity => {
            ModelUtils.hide(entity, true);
        });
    }
    stopFadeMaterialAnimation() {
        if (this.materialFadeInterval >= 0) {
            window.clearInterval(this.materialFadeInterval);
            this.materialFadeInterval = -1;
        }
        this.materialOpacity = TRANSPARENT_MATERIAL_OPACITY;
        this.viewer?.set_opacity(this.transparentMaterialId, this.materialOpacity);
    }
    startFadeMaterialAnimation() {
        /**
         * Do not start the fade animation if there is something on focus. The
         * camera update could call to start while hovering a product, after the
         * add hover component has happened.
         */
        if (this.coreServices.entityManager.entities
            .filter(entity => entity.hasComponent(HideOccludingObjectsComponentIds.FADE_MATERIAL_COMPONENT_ID))
            .some((entity) => this.entityHasHoverFocusOrSelected(entity))) {
            return;
        }
        this.stopFadeMaterialAnimation();
        this.materialFadeInterval = window.setInterval(() => {
            if (!this.viewer) {
                return;
            }
            this.materialOpacity -= (FADE_OUT_UPDATE_TIME * TRANSPARENT_MATERIAL_OPACITY) / FADE_OUT_TIME;
            if (this.materialOpacity > 0) {
                this.viewer.set_opacity(this.transparentMaterialId, this.materialOpacity);
                return;
            }
            window.clearInterval(this.materialFadeInterval);
            this.viewer.set_opacity(this.transparentMaterialId, 0);
            this.hideFadedEntities();
        }, FADE_OUT_UPDATE_TIME);
    }
    getEntityViewerObjectIds(entity) {
        const viewerObjectComponent = entity.getComponent(ComponentIds.VIEWER_OBJECT_COMPONENT_ID);
        if (!viewerObjectComponent || !this.viewer) {
            return [];
        }
        const viewerObjectId = viewerObjectComponent.viewerObject.getViewerObjectId();
        return this.viewer.get_children(viewerObjectId, false, true);
    }
    applyMaterialForEntity(entity, materialId) {
        if (!this.viewer)
            return;
        const viewerObjectIds = this.getEntityViewerObjectIds(entity);
        for (const viewerObjectId of viewerObjectIds) {
            this.viewer.set_material(viewerObjectId, materialId).catch(error => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
        }
    }
    applyTransparentMaterial(entity) {
        if (this.transparentMaterialId >= 0) {
            this.applyMaterialForEntity(entity, this.transparentMaterialId);
        }
    }
    removeTransparentMaterial(entity) {
        this.applyMaterialForEntity(entity, null);
    }
}
//# sourceMappingURL=FadeMaterialSystem.js.map